import React from 'react';

import type { FormField } from '../types/pdf.types';

interface RadioFieldOverlayProps {
  field: FormField;
  checked: boolean;
  onChange: (value: string | boolean | string[]) => void;
  scale: number;
  isZooming?: boolean;
}

const RadioFieldOverlay: React.FC<RadioFieldOverlayProps> = ({
  field,
  checked,
  onChange,
  scale,
  isZooming = false,
}) => {
  if (field.hidden) return null;

  const [x, y, width, height] = field.rect;
  const scaledX = x * scale;
  const scaledY = y * scale;
  const scaledWidth = width * scale;
  const scaledHeight = height * scale;

  const isReadOnly = field.readOnly === true;
  const isRequired = field.required === true;
  const isNoExport = field.noExport === true;

  const overlayStyle = {
    left: `${scaledX}px`,
    top: `${scaledY}px`,
    width: `${scaledWidth}px`,
    height: `${scaledHeight}px`,
    // eslint-disable-next-line no-nested-ternary
    opacity: isZooming ? 0 : isReadOnly ? 0.6 : 1,
    pointerEvents: isZooming ? 'none' : 'auto',
    cursor: isReadOnly ? 'not-allowed' : 'pointer',
  };

  const overlayClassName = [
    'pdf-field-overlay',
    'pdf-field-toggle',
    isRequired ? 'pdf-field-toggle--required' : '',
    isNoExport ? 'pdf-field-toggle--no-export' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const titleParts = [field.name];
  if (isRequired) titleParts.push('Required');
  if (isNoExport) titleParts.push('No export');
  if (isReadOnly) titleParts.push('Read-only');
  const title = titleParts.join(' • ');

  return (
    // eslint-disable-next-line jsx-a11y/role-supports-aria-props
    <input
      type="radio"
      name={field.radioGroup || field.name}
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      className={overlayClassName}
      style={overlayStyle}
      title={title}
      disabled={isReadOnly}
      required={isRequired}
      aria-required={isRequired}
    />
  );
};

const areEqual = (
  prevProps: RadioFieldOverlayProps,
  nextProps: RadioFieldOverlayProps,
) => {
  return (
    prevProps.field.id === nextProps.field.id &&
    prevProps.checked === nextProps.checked &&
    prevProps.scale === nextProps.scale &&
    prevProps.isZooming === nextProps.isZooming
  );
};

export default React.memo(RadioFieldOverlay, areEqual);
